import type { MouseEventHandler, PropsWithChildren } from 'react';

import { NextImageWithLoader } from '@/components/NextImageWithLoader';
import type { ServicePerkType } from '@/integrations/contentful';
import { generateDataTestId } from '@/lib/tests';

import buyingAStrap from '@/assets/icons/buyingAStrap.svg';
import buyingAWatch from '@/assets/icons/buyingAWatch.svg';
import easyContact from '@/assets/icons/concierge.svg';
import engraving from '@/assets/icons/engraving.svg';
import giftOffered from '@/assets/icons/give-gift.svg';
import pickUpInBoutique from '@/assets/icons/pickUpInBoutique.svg';
import freeReturn from '@/assets/icons/return.svg';
import securePayments from '@/assets/icons/securePayment.svg';
import serviceYourWatch from '@/assets/icons/serviceYourWatch.svg';
import freeShipping from '@/assets/icons/shipping.svg';
import warranty from '@/assets/icons/warranty.svg';

import * as S from './Perk.styles';

const SIZES = {
  small: 40,
  normal: 55,
  large: 72,
};
const ICONS_MAP: Record<ServicePerkType, string> = {
  'easy contact': easyContact,
  buyingAWatch: buyingAWatch,
  buyingAStrap: buyingAStrap,
  pickUpInBoutique: pickUpInBoutique,
  serviceYourWatch: serviceYourWatch,
  'free return': freeReturn,
  'free shipping': freeShipping,
  'gift service': giftOffered,
  engraving: engraving,
  warranty: warranty,
  'secure payment': securePayments,
};

export interface PerkProps extends PropsWithChildren {
  iconSize?: 'normal' | 'small' | 'large';
  onClick?: MouseEventHandler<HTMLElement>;
  type: ServicePerkType;
  variant?: S.Variant;
}

export const Perk = ({
  children,
  iconSize = 'normal',
  onClick,
  type,
  variant = 'grey',
}: PerkProps) => (
  <S.Wrapper data-test-id='PerkWrapper' onClick={onClick}>
    <S.IconWrapper
      data-test-id='PerkIconWrapper'
      variant={variant}
      {...generateDataTestId('item', 'perk')}
    >
      <NextImageWithLoader
        alt={type}
        data-test-id='PerkIcon'
        height={SIZES[iconSize]}
        priority
        src={ICONS_MAP[type]}
        width={SIZES[iconSize]}
      />
    </S.IconWrapper>
    {children}
  </S.Wrapper>
);

Perk.displayName = 'Perk';
